/**
 * Generic FileTree utilities for building hierarchical file tree structures.
 * Supports both Diff-based trees (mule-refactor) and canvas-based trees (landing page).
 */

export interface FileTreeNode {
  name: string
  path: string
  type: 'file' | 'folder'
  children?: FileTreeNode[]
  isExpanded?: boolean
  // Generic data field for storing associated data (Diff, CanvasData, etc.)
  data?: unknown
}

/**
 * Builds a hierarchical tree structure from an array of file paths
 * @param files Array of objects with filePath and optional data
 * @returns Root node of the file tree
 */
export function buildFileTreeFromPaths<T>(
  files: Array<{ filePath: string; data?: T }>
): FileTreeNode {
  const root: FileTreeNode = {
    name: 'root',
    path: '',
    type: 'folder',
    children: [],
    isExpanded: true
  }

  files.forEach(({ filePath, data }) => {
    const pathSegments = filePath.split('/').filter(segment => segment.length > 0)

    let currentNode = root
    let currentPath = ''

    pathSegments.forEach((segment, index) => {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment
      const isLastSegment = index === pathSegments.length - 1

      if (!currentNode.children) {
        currentNode.children = []
      }

      let childNode = currentNode.children.find(child => child.name === segment)

      if (!childNode) {
        childNode = {
          name: segment,
          path: currentPath,
          type: isLastSegment ? 'file' : 'folder',
          isExpanded: true,
          ...(isLastSegment ? { data } : { children: [] })
        }
        currentNode.children.push(childNode)
      }

      currentNode = childNode
    })
  })

  // Sort children: folders first, then files, alphabetically
  sortTreeNodes(root)

  return root
}

/**
 * Recursively sorts tree nodes (folders first, then files, alphabetically)
 */
function sortTreeNodes(node: FileTreeNode) {
  if (node.children) {
    node.children.sort((a, b) => {
      // Folders before files
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1
      }
      // Alphabetical
      return a.name.localeCompare(b.name)
    })

    // Recursively sort children
    node.children.forEach(child => sortTreeNodes(child))
  }
}

/**
 * Finds a node in the tree by its path
 */
export function findNodeByPath(root: FileTreeNode, path: string): FileTreeNode | null {
  if (root.path === path) {
    return root
  }

  if (root.children) {
    for (const child of root.children) {
      const found = findNodeByPath(child, path)
      if (found) {
        return found
      }
    }
  }

  return null
}

/**
 * Toggles the expanded state of a node
 */
export function toggleNodeExpanded(root: FileTreeNode, path: string): FileTreeNode {
  if (root.path === path && root.type === 'folder') {
    return { ...root, isExpanded: !root.isExpanded }
  }

  if (root.children) {
    return {
      ...root,
      children: root.children.map(child => toggleNodeExpanded(child, path))
    }
  }

  return root
}

/**
 * Gets all file paths from the tree (for navigation)
 */
export function getAllFilePaths(node: FileTreeNode): string[] {
  const paths: string[] = []

  if (node.type === 'file') {
    paths.push(node.path)
  }

  if (node.children) {
    node.children.forEach(child => {
      paths.push(...getAllFilePaths(child))
    })
  }

  return paths
}
